import { Color, Theme, ThemeOptions, createTheme } from '@mui/material';
import { deepmerge } from '@mui/utils';

import { defaultTheme } from './default-theme';
import { getPalette } from './helpers';
import { commonMixins } from './mixins';

const grey: Color = {
  50: '#DCE6F0',
  100: '#D1D9E4',
  200: '#ABB4BF',
  300: '#939CA6',
  400: '#707983',
  500: '#5A646E',
  600: '#454D56',
  700: '#282F37',
  800: '#191F26',
  900: '#0B1015',
  A100: '',
  A200: '',
  A400: '',
  A700: '',
};
const blue: Color = {
  50: '#DCE6F0',
  100: '#D1D9E4',
  200: '#ABB4BF',
  300: '#939CA6',
  400: '#657C93',
  500: '#5B728A',
  600: '#364B5F',
  700: '#1D2A37',
  800: '#101C26',
  900: '#0F151C',
  A100: '',
  A200: '',
  A400: '',
  A700: '',
};
const mapBlue: Color = {
  50: '#DCE6F0',
  100: '#D1D9E4',
  200: '#ABB4BF',
  300: '#939CA6',
  400: '#657C93',
  500: '#5B728A',
  600: '#364B5F',
  700: '#1D2A37',
  800: '#101C26',
  900: '#0F151C',
  A100: '',
  A200: '',
  A400: '',
  A700: '',
};
const green: Color = {
  50: '#DCE6F0',
  100: '#D1D9E4',
  200: '#ABB4BF',
  300: '#939CA6',
  400: '#657C93',
  500: '#5B728A',
  600: '#364B5F',
  700: '#1D2A37',
  800: '#101C26',
  900: '#0F151C',
  A100: '',
  A200: '',
  A400: '',
  A700: '',
};
const orange: Color = {
  50: '#DCE6F0',
  100: '#D1D9E4',
  200: '#ABB4BF',
  300: '#939CA6',
  400: '#657C93',
  500: '#5B728A',
  600: '#364B5F',
  700: '#1D2A37',
  800: '#101C26',
  900: '#0F151C',
  A100: '',
  A200: '',
  A400: '',
  A700: '',
};
const violet: Color = {
  50: '#DCE6F0',
  100: '#D1D9E4',
  200: '#ABB4BF',
  300: '#939CA6',
  400: '#657C93',
  500: '#5B728A',
  600: '#364B5F',
  700: '#1D2A37',
  800: '#101C26',
  900: '#0F151C',
  A100: '',
  A200: '',
  A400: '',
  A700: '',
};
const mint: Color = {
  50: '#DCE6F0',
  100: '#D1D9E4',
  200: '#ABB4BF',
  300: '#939CA6',
  400: '#657C93',
  500: '#5B728A',
  600: '#364B5F',
  700: '#1D2A37',
  800: '#101C26',
  900: '#0F151C',
  A100: '',
  A200: '',
  A400: '',
  A700: '',
};
const yellow: Color = {
  50: '#DCE6F0',
  100: '#D1D9E4',
  200: '#ABB4BF',
  300: '#939CA6',
  400: '#657C93',
  500: '#5B728A',
  600: '#364B5F',
  700: '#1D2A37',
  800: '#101C26',
  900: '#0F151C',
  A100: '',
  A200: '',
  A400: '',
  A700: '',
};
// TMDXNC-68 — the NOW line is a status colour, not mode-dependent chrome, so it
// carries the same value here. Defined in both themes on purpose: `Palette`
// declares it required while `PaletteOptions` does not, so a token added to only
// one theme type-checks and is then `undefined` at runtime in the other.
const hubDashboard = {
  nowTime: '#FF0000',
  nowLine: '#FF000080',
  // Shift colours are status, not mode-dependent chrome, so they match the dark
  // theme exactly. See the dark theme for their provenance.
  shift: {
    available: 'rgba(217, 217, 217, 0.4)',
    availableHighlight: 'rgba(255, 255, 255, 0.8)',
    noCall: 'rgba(217, 217, 217, 0.4)',
    travel: 'rgba(217, 217, 217, 0.4)',
    onCall: '#0E9F6E',
    extraOnCall: '#56AE9F',
    atHub: '#1C64F2',
    phoneSupport: '#6875F5',
    absence: '#ED9F07',
    unavailable: '#F05252',
  },
};

export const lightTheme: Theme = createTheme(
  deepmerge(defaultTheme, {
    // Configure the grid to use 24 columns
    components: {
      MuiGrid: {
        defaultProps: {
          columns: 24, // Set the default number of columns to 24
        },
      },
      // Include any other light-theme specific component overrides here
    },
    typography: {
      blue: {
        color: '#1680FE',
      },
      green: {
        color: '#46A000',
      },
      violet: {
        color: '#A363E2',
      },
    },
    palette: {
      ...getPalette({
        mode: 'light',
        background: '#ffffff',
        primary: '#0b1015',
        grey,
        blue,
        mapBlue,
        green,
        violet,
        orange,
        mint,
        yellow,
      }),
      hubDashboard,
    },
    // Add mixins
    mixins: commonMixins,
  } as ThemeOptions),
);
